import { Box } from '@coolblue-development/becky';
import { IconButton, Loader } from '@coolblue-development/freddy';
import { Cross } from '@coolblue-development/icons';
import { Attachment } from '../../../../../../models/Attachment';
import BaseAttachmentChip from '../../BaseAttachmentChip';
import { PendingUpload } from './useAttachmentUpload';

const REMOVAL_ERROR_COLOR = '#D71F1F';
const REMOVE_ICON_COLOR = '#868A90';

interface DraftAttachmentChipsProps {
  attachments: Attachment[];
  pendingUploads?: PendingUpload[];
  onDismiss?: (id: string) => void;
  onRemove?: (attachmentId: string) => void;
  removingAttachmentIds?: Set<string>;
  attachmentRemovalErrors?: Record<string, string>;
}

const DraftAttachmentChips = ({
  attachments,
  pendingUploads = [],
  onDismiss,
  onRemove,
  removingAttachmentIds,
  attachmentRemovalErrors = {}
}: DraftAttachmentChipsProps): JSX.Element | null => {
  if (attachments.length === 0 && pendingUploads.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '12px'
      }}
      data-testid="attachment-chips-container"
    >
      {attachments.map(attachment => {
        const isRemoving = removingAttachmentIds?.has(attachment.id) ?? false;
        const removalError = attachmentRemovalErrors[attachment.id];

        return (
          <Box key={attachment.id} sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <BaseAttachmentChip
              fileName={attachment.name}
              dataTestId={`attachment-chip-${attachment.id}`}
              variant={isRemoving ? 'loading' : removalError ? 'error' : 'default'}
              trailing={
                isRemoving ? (
                  <Box sx={{ position: 'relative', marginRight: '8px' }}>
                    <Loader size="small" />
                  </Box>
                ) : (
                  <IconButton
                    size="small"
                    variant="transparent"
                    aria-label={`Remove ${attachment.name}`}
                    disabled={isRemoving}
                    onClick={() => onRemove?.(attachment.id)}
                    data-testid={`attachment-remove-${attachment.id}`}
                  >
                    <Cross color={removalError ? REMOVAL_ERROR_COLOR : REMOVE_ICON_COLOR} />
                  </IconButton>
                )
              }
            />

            {removalError && (
              <Box
                sx={{ fontSize: '12px', color: REMOVAL_ERROR_COLOR }}
                data-testid={`attachment-remove-error-${attachment.id}`}
              >
                {removalError}
              </Box>
            )}
          </Box>
        );
      })}

      {pendingUploads.map(upload =>
        upload.status === 'uploading' ? (
          <BaseAttachmentChip
            key={upload.id}
            variant="loading"
            fileName={upload.name}
            dataTestId={`attachment-chip-uploading-${upload.id}`}
            trailing={
              <Box sx={{ position: 'relative', marginRight: '8px' }}>
                <Loader size="small" />
              </Box>
            }
          />
        ) : (
          <BaseAttachmentChip
            key={upload.id}
            variant="error"
            fileName={upload.name}
            dataTestId={`attachment-chip-error-${upload.id}`}
            trailing={
              <button
                type="button"
                aria-label={`Dismiss ${upload.name}`}
                onClick={() => onDismiss?.(upload.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  cursor: 'pointer'
                }}
              >
                <Cross color="#D71F1F" />
              </button>
            }
          />
        )
      )}
    </Box>
  );
};

export default DraftAttachmentChips;
